/**
 * Linked History Panel Component
 * Colaiste Lurgan Management System — surfaces other enrolments confirmed
 * as the same person via the Claru identity review queue (or the historical
 * backfill). Staff-only (manage_students): parent portal access deliberately
 * stays scoped to each row's own parent_email, so this is the one place a
 * mismatch (e.g. a different parent registering in a later year) is visible.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { UserSearch } from 'lucide-react';
import { api } from '../../lib/api';

const STATUS_CLASSES = {
  ACTIVE:    'bg-green-600 text-white border-transparent',
  COMPLETED: 'bg-blue-600 text-white border-transparent',
  ARCHIVED:  'bg-gray-500 text-white border-transparent',
  CANCELLED: 'bg-red-600 text-white border-transparent',
};

export function LinkedHistoryPanel({ student }) {
  const { t } = useTranslation();

  const { data } = useQuery({
    queryKey: ['student-linked-enrolments', student?.uuid],
    queryFn: () => api.students.getLinkedEnrolments(student.uuid),
    enabled: !!student?.uuid,
  });

  const enrolments = data ?? [];
  if (enrolments.length === 0) return null;

  return (
    <Card>
      <CardHeader className="bg-muted/40 border-b border-border py-3 px-4">
        <CardTitle className="flex items-center text-sm font-semibold text-foreground">
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F28482]/10 mr-3">
            <UserSearch className="h-4 w-4 text-[#F28482]" />
          </span>
          {t('linkedHistory.title')}
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-4 px-0 pb-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/20">
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">{t('linkedHistory.columns.course')}</th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">{t('linkedHistory.columns.schoolYear')}</th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">{t('linkedHistory.columns.status')}</th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">{t('linkedHistory.columns.parent')}</th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">{t('linkedHistory.columns.parentEmail')}</th>
              </tr>
            </thead>
            <tbody>
              {enrolments.map((enrolment) => (
                <tr key={enrolment.uuid} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-2.5">{enrolment.course_name || '—'}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{enrolment.school_year_name || '—'}</td>
                  <td className="px-4 py-2.5">
                    <Badge className={STATUS_CLASSES[enrolment.status] || 'bg-gray-400 text-white border-transparent'}>
                      {t(`students.status.${(enrolment.status || '').toLowerCase()}`, enrolment.status)}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{enrolment.parent_name || '—'}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{enrolment.parent_email || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default LinkedHistoryPanel;
